import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, ListGroup, Tabs, Tab, Spinner } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { getMyNotifications } from "@/api/notificationsApi";
import type { NotificationEventDto } from "@/types/dashboard";
import "./Notifications.scss";

const MyNotificationsPage: React.FC = () => {
  const [events, setEvents] = useState<NotificationEventDto[]>([]);
  const [openEvents, setOpenEvents] = useState<NotificationEventDto[]>([]);
  const [activeEventId, setActiveEventId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      try {
        const data = await getMyNotifications();
        setEvents(data);
      } catch (err) {
        console.error("Failed to load notifications", err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const queryOpenId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("open");
  }, [location.search]);

  // When events + query are ready, open the requested tab if present
  useEffect(() => {
    if (!events.length || !queryOpenId) return;

    const target = events.find((e) => e.eventGuid === queryOpenId);
    if (!target) return;

    setOpenEvents((prev) =>
      prev.some((p) => p.eventGuid === target.eventGuid)
        ? prev
        : [...prev, target]
    );
    setActiveEventId(target.eventGuid);
  }, [events, queryOpenId]);

  const handleOpenEvent = (ev: NotificationEventDto) => {
    setOpenEvents((prev) =>
      prev.some((p) => p.eventGuid === ev.eventGuid)
        ? prev
        : [...prev, ev]
    );
    setActiveEventId(ev.eventGuid);
    navigate(`/notifications?open=${ev.eventGuid}`, { replace: true });
  };

  const handleCloseTab = (eventGuid: string) => {
    setOpenEvents((prev) => prev.filter((p) => p.eventGuid !== eventGuid));
    if (activeEventId === eventGuid) {
      const next = openEvents.find((e) => e.eventGuid !== eventGuid);
      setActiveEventId(next ? next.eventGuid : null);
      if (next) {
        navigate(`/notifications?open=${next.eventGuid}`, { replace: true });
      } else {
        navigate("/notifications", { replace: true });
      }
    }
  };

  return (
    <div className="container-fluid"
      style={{
          paddingLeft: "0"
        }}>
      <Row className="notifications-row g-0">
        {/* Left column: list of events */}
        <Col md={4} className="notifications-left">
          <Card className="enmax-card">
            <div className="enmax-card-header">
              <span>My Notifications</span>
            </div>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : events.length === 0 ? (
                <div className="text-muted small p-3">
                  No notifications found.
                </div>
              ) : (
                <div className="notification-list">
                  <ListGroup variant="flush">
                    {events.map((n) => (
                      <ListGroup.Item
                        key={n.eventGuid}
                        action
                        className={`notification-list-item ${n.eventGuid === activeEventId ? "selected" : ""}`}
                        onClick={() => handleOpenEvent(n)}
                      >
                        <div className="fw-semibold">{n.notificationType}</div>
                        <div className="small text-muted">
                          {new Date(n.createdDateTime).toLocaleString()}
                        </div>
                        <div className="small text-muted">
                          {n.groups.length} group
                          {n.groups.length === 1 ? "" : "s"}
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Right column: tabs with details */}
        <Col md={8} className="notifications-right">
          <Card className="enmax-card">
            <div className="enmax-card-header">
              <span>Notification Details</span>
            </div>
            <Card.Body>
              {openEvents.length === 0 ? (
                <div className="text-muted small">
                  Select a notification to view details.
                </div>
              ) : (
                <Tabs
                  className="notification-tabs"
                  activeKey={activeEventId ?? undefined}
                  onSelect={(k) => setActiveEventId(k as string)}
                >
                  {openEvents.map((ev) => (
                    <Tab
                      key={ev.eventGuid}
                      eventKey={ev.eventGuid}
                      title={
                        <>
                          {ev.notificationType}{" "}
                          <span
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCloseTab(ev.eventGuid);
                            }}
                          >
                            ×
                          </span>
                        </>
                      }
                    >
                      <NotificationDetails event={ev} />
                    </Tab>
                  ))}
                </Tabs>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

interface DetailsProps {
  event: NotificationEventDto;
}

const NotificationDetails: React.FC<DetailsProps> = ({ event }) => {
  return (
    <div className="pt-3">
      <h5 className="mb-2">{event.notificationType}</h5>
      <div className="small text-muted mb-2">
        Sent: {new Date(event.createdDateTime).toLocaleString()}
      </div>
      <div className="notification-message-wrapper">
        <div
          className="notification-message"
          dangerouslySetInnerHTML={{ __html: event.notificationContent }}
        />
      </div>

      <h6 className="mt-3">Impacted Groups</h6>
      <ListGroup variant="flush">
        {event.groups.map((g) => (
          <ListGroup.Item key={g.groupId}
            className="notification-list-item">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div>{g.displayName}</div>
                <div className="small text-muted">{g.mail}</div>
              </div>
              {/* If you have an internal resource mapping, convert here later */}
            </div>
          </ListGroup.Item>
        ))}
      </ListGroup>
    </div>
  );
};

export default MyNotificationsPage;